import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    InvoiceTemplateRow,
    InvoiceTemplatesTableCallbacks,
} from "../invoiceTemplates.types";

export const ActionsCell = memo(function ActionsCell({
    row,
    table,
}: CellContext<ViewTableFeatures, InvoiceTemplateRow, string>) {
    const { callbacks } = useViewTableMeta<
        InvoiceTemplatesTableCallbacks,
        never
    >(table);
    const template = row.original;
    const isArchived = template.archivedAt != null;

    // Templates that have been used to create at least one invoice can't be
    // deleted (app-level guard backed by sourceTemplateId lineage) — disable
    // Delete up front with an explanatory tooltip instead of letting the user
    // hit the 409 toast. Mirrors items/Cells/ActionsCell.tsx's
    // isReferencedByInvoices handling for catalog items.
    const isReferencedByInvoices = template.hasSourcedInvoices;

    return (
        <ViewTableCell captureClicks>
            <HStack gap={8} align="center" justify="end">
                {!isArchived && (
                    <Button
                        variant="primary"
                        size="md"
                        className={viewTableStyles.showOnHover}
                        onClick={() => callbacks?.onCreateInvoice(template)}
                    >
                        Create invoice
                    </Button>
                )}
                <Menu.More className={viewTableStyles.showOnHover}>
                    <Menu.Item onClick={() => callbacks?.onEdit(template)}>
                        Edit
                    </Menu.Item>
                    <Menu.Item onClick={() => callbacks?.onDuplicate(template)}>
                        Duplicate
                    </Menu.Item>
                    <Menu.Item
                        onClick={() => callbacks?.onArchiveToggle(template)}
                    >
                        {isArchived ? "Unarchive" : "Archive"}
                    </Menu.Item>
                    <Menu.Item
                        destructive
                        disabled={isReferencedByInvoices}
                        disabledReason="Template is used by one or more invoices and can't be deleted; archive it instead"
                        onClick={() => callbacks?.onDelete(template)}
                    >
                        Delete
                    </Menu.Item>
                </Menu.More>
            </HStack>
        </ViewTableCell>
    );
});
